import { useMemo, useState, type ReactNode } from 'react'
import { Badge, Card, Drawer, DrawerSection, Empty, Field, RowsSkeleton, SearchInput, Switch } from './ui.js'
import { CartIcon, CheckIcon, PlusIcon, XIcon } from './icons.js'
import { money } from '../lib/format.js'
import {
  useBundles,
  useProductSearch,
  type Bundle,
  type BundleDraft,
  type BundleItem,
  type VariantHit,
} from '../hooks/useBundles.js'

/**
 * Building a "frequently bought together" offer.
 *
 * ── What an offer actually is ───────────────────────────────────────────────
 *
 * A trigger and a list of recommendations. The trigger is one product — or none,
 * meaning every product — and the recommendations are what a shopper is shown
 * alongside it. The prices are not stored anywhere: they are read from the
 * merchant's live catalogue every time the widget is drawn, so what is on screen
 * here is a picker, not a price list.
 *
 * ── Why the picker only ever adds variants ──────────────────────────────────
 *
 * A cart line is a variant, not a product. "Blue Shirt" cannot be added to a
 * basket; "Blue Shirt / M" can. Products with one variant read as themselves, so
 * the distinction costs nothing in the common case and is the difference between
 * a working checkbox and a broken one for everybody else.
 */

const BLANK: BundleDraft = {
  name: '',
  active: true,
  main_variant_id: null,
  main_product_id: null,
  main_title: null,
  items: [],
  discount_type: 'percent',
  discount_value: 10,
  button_text: 'Claim Offer',
  default_selected: true,
  max_products: 6,
  starts_at: null,
  ends_at: null,
}

export function BundleList(): ReactNode {
  const { bundles, isPending, error, create, update, remove, writeError, saving } = useBundles()
  const [editing, setEditing] = useState<Bundle | 'new' | null>(null)

  const draft = editing === 'new' ? BLANK : editing ? toDraft(editing) : null

  async function save(next: BundleDraft): Promise<void> {
    if (editing === 'new') await create.mutateAsync(next)
    else if (editing) await update.mutateAsync({ id: editing.id, body: next })

    setEditing(null)
  }

  return (
    <>
      <Card
        title="Offers"
        subtitle="What each product recommends. Without one, the widget has nothing to show."
        action={
          <button type="button" className="btn btn--sm" onClick={() => setEditing('new')}>
            <PlusIcon size={15} />
            New offer
          </button>
        }
      >
        {isPending ? (
          <RowsSkeleton rows={3} />
        ) : error ? (
          <Empty error title="Could not load your offers" icon={<CartIcon size={20} />}>
            {error.message}
          </Empty>
        ) : bundles.length === 0 ? (
          <Empty
            title="No offers yet"
            icon={<CartIcon size={20} />}
            action={
              <button type="button" className="btn btn--sm" onClick={() => setEditing('new')}>
                Create the first one
              </button>
            }
          >
            Turning the feature on above is only half of it — an offer is what says which products to
            recommend.
          </Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Offer</th>
                  <th>Shows on</th>
                  <th>Recommends</th>
                  <th>Discount</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {bundles.map((bundle) => (
                  <tr key={bundle.id} className="is-clickable" onClick={() => setEditing(bundle)}>
                    <td>
                      <span className="table__cell">
                        <span>
                          {bundle.name}
                          <span className="table__sub">{bundle.button_text}</span>
                        </span>
                      </span>
                    </td>

                    <td>
                      {bundle.main_title ?? (
                        <span className="faint">Every product</span>
                      )}
                    </td>

                    <td className="num">{bundle.items.length}</td>

                    <td>
                      {bundle.discount_label === '' ? <span className="faint">—</span> : bundle.discount_label}
                    </td>

                    {/*
                      Live, not active. A merchant who ticks "active" and sees
                      nothing on their store has no way to tell that the end date
                      passed last week or that every recommended product was
                      deleted — so the column answers that instead of repeating
                      the switch they just flipped.
                    */}
                    <td>
                      {bundle.live ? (
                        <Badge tone="ok" dot>
                          Live
                        </Badge>
                      ) : (
                        <Badge tone="outline" dot>
                          {reasonNotLive(bundle)}
                        </Badge>
                      )}
                    </td>

                    <td className="right">
                      <button
                        type="button"
                        className="icon-btn"
                        aria-label={`Delete ${bundle.name}`}
                        onClick={(event) => {
                          event.stopPropagation()
                          if (confirm(`Delete "${bundle.name}"? Shoppers will stop seeing it immediately.`)) {
                            remove.mutate(bundle.id)
                          }
                        }}
                      >
                        <XIcon size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {draft && (
        <BundleForm
          key={editing === 'new' ? 'new' : editing?.id}
          initial={draft}
          isNew={editing === 'new'}
          saving={saving}
          error={writeError?.message ?? null}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </>
  )
}

/**
 * Why an offer is not showing, in three words.
 *
 * Ordered by what a merchant is most likely to have done, not by how the server
 * evaluates it — somebody who just unticked the switch should read "Paused"
 * rather than the date problem they also have.
 */
function reasonNotLive(bundle: Bundle): string {
  if (!bundle.active) return 'Paused'
  if (bundle.items.length === 0) return 'No products'

  const now = Date.now()
  if (bundle.starts_at && new Date(bundle.starts_at).getTime() > now) return 'Scheduled'
  if (bundle.ends_at && new Date(bundle.ends_at).getTime() < now) return 'Ended'

  return 'Not live'
}

function BundleForm({
  initial,
  isNew,
  saving,
  error,
  onClose,
  onSave,
}: {
  initial: BundleDraft
  isNew: boolean
  saving: boolean
  error: string | null
  onClose: () => void
  onSave: (next: BundleDraft) => Promise<void>
}): ReactNode {
  const [value, setValue] = useState<BundleDraft>(initial)

  function set<K extends keyof BundleDraft>(field: K, next: BundleDraft[K]): void {
    setValue((previous) => ({ ...previous, [field]: next }))
  }

  // Both are enforced by the server too. Checked here so the reason appears
  // beside the field rather than as a validation error after a round trip.
  const problem =
    value.name.trim() === ''
      ? 'Give the offer a name — only you will see it.'
      : value.items.length === 0
        ? 'Add at least one product to recommend.'
        : null

  return (
    <Drawer
      open
      onClose={onClose}
      title={isNew ? 'New offer' : value.name || 'Offer'}
      subtitle="Prices come from your catalogue when a shopper opens the page, never from here."
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={saving || problem !== null}
            onClick={() => void onSave(value)}
          >
            {saving ? 'Saving…' : isNew ? 'Create offer' : 'Save changes'}
          </button>
        </>
      }
    >
      <DrawerSection title="Basics">
        <Field label="Name" hint="For your own reference. Shoppers never see it.">
          <input
            className="input"
            value={value.name}
            maxLength={120}
            placeholder="Socks with every pair of shoes"
            onChange={(event) => set('name', event.target.value)}
          />
        </Field>

        <Switch
          label="Active"
          checked={value.active}
          onChange={(next) => set('active', next)}
        />
      </DrawerSection>

      <DrawerSection title="Shows on">
        {/*
          Empty means every product, and that is the useful default — a store
          with one offer should not have to attach it to each item in their
          catalogue one at a time. A specific product wins over the catch-all
          when both exist, so the two coexist.
        */}
        {value.main_variant_id ? (
          <div className="row" style={{ justifyContent: 'space-between', gap: 12 }}>
            <span>{value.main_title ?? 'Selected product'}</span>
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              onClick={() => {
                set('main_variant_id', null)
                set('main_product_id', null)
                set('main_title', null)
              }}
            >
              Show on every product
            </button>
          </div>
        ) : (
          <ProductPicker
            label="Every product — pick one to narrow it"
            chosen={[]}
            onPick={(hit) => {
              set('main_variant_id', hit.variant_id)
              set('main_product_id', hit.product_id)
              set('main_title', labelFor(hit))
            }}
          />
        )}
      </DrawerSection>

      <DrawerSection title={`Recommends (${value.items.length})`}>
        <ChosenItems items={value.items} onRemove={(id) => set('items', value.items.filter((i) => i.variant_id !== id))} />

        <ProductPicker
          label="Add a product"
          chosen={value.items.map((entry) => entry.variant_id)}
          onPick={(hit) => {
            if (value.items.some((entry) => entry.variant_id === hit.variant_id)) return
            set('items', [...value.items, toItem(hit)])
          }}
        />

        <Field
          label="Show at most"
          hint="Lowering this hides the extras rather than deleting them, so you can raise it again."
        >
          <input
            type="number"
            className="input"
            min={1}
            max={20}
            value={value.max_products}
            onChange={(event) => set('max_products', clamp(Number(event.target.value), 1, 20))}
          />
        </Field>
      </DrawerSection>

      <DrawerSection title="Discount">
        <Field
          label="Type"
          hint="Applies to the recommended products only — not to the one the shopper already chose."
        >
          <select
            className="input"
            value={value.discount_type}
            onChange={(event) => set('discount_type', event.target.value as BundleDraft['discount_type'])}
          >
            <option value="percent">Percentage off</option>
            <option value="fixed">Fixed amount off</option>
          </select>
        </Field>

        <Field
          label={value.discount_type === 'percent' ? 'Percent off' : 'Amount off'}
          hint={value.discount_value <= 0 ? 'Zero runs it as a plain recommendation, with no discount badge.' : undefined}
        >
          <input
            type="number"
            className="input"
            min={0}
            step={value.discount_type === 'percent' ? 1 : 10}
            value={value.discount_value}
            onChange={(event) => set('discount_value', Math.max(0, Number(event.target.value) || 0))}
          />
        </Field>
      </DrawerSection>

      <DrawerSection title="Button">
        <Field label="Button text">
          <input
            className="input"
            value={value.button_text}
            maxLength={60}
            placeholder="Claim Offer"
            onChange={(event) => set('button_text', event.target.value)}
          />
        </Field>

        <Switch
          label="Tick the products by default"
          checked={value.default_selected}
          onChange={(next) => set('default_selected', next)}
        />
      </DrawerSection>

      <DrawerSection title="Schedule">
        <Field label="Starts" hint="Leave both blank to run it until you pause it.">
          <input
            type="datetime-local"
            className="input"
            value={forInput(value.starts_at)}
            onChange={(event) => set('starts_at', event.target.value || null)}
          />
        </Field>

        <Field label="Ends">
          <input
            type="datetime-local"
            className="input"
            value={forInput(value.ends_at)}
            onChange={(event) => set('ends_at', event.target.value || null)}
          />
        </Field>
      </DrawerSection>

      {/* Beside the button it blocks, rather than in the footer -- that is a
          flex row of equal-width buttons, and prose in it lands between them. */}
      {(error ?? problem) && (
        <p className="muted" style={{ fontSize: '.82rem', color: error ? 'var(--bad)' : undefined }}>
          {error ?? problem}
        </p>
      )}
    </Drawer>
  )
}

function ChosenItems({
  items,
  onRemove,
}: {
  items: BundleItem[]
  onRemove: (variantId: string) => void
}): ReactNode {
  if (items.length === 0) {
    return (
      <p className="muted" style={{ fontSize: '.85rem' }}>
        Nothing chosen yet. An offer with no recommendations never goes live.
      </p>
    )
  }

  return (
    <ul className="picker__chosen">
      {items.map((entry) => (
        <li key={entry.variant_id}>
          {entry.image ? <img src={entry.image} alt="" loading="lazy" /> : <span className="picker__blank" />}
          <span className="picker__text">{entry.title ?? entry.variant_id}</span>
          <button
            type="button"
            className="icon-btn"
            aria-label={`Remove ${entry.title ?? 'product'}`}
            onClick={() => onRemove(entry.variant_id)}
          >
            <XIcon size={15} />
          </button>
        </li>
      ))}
    </ul>
  )
}

/**
 * Search the store's catalogue and pick a variant.
 *
 * Only searches once opened. The results cost a Shopify API call against the
 * merchant's own rate limit, and a drawer with two of these on it would spend
 * two of them before anybody had typed anything.
 */
function ProductPicker({
  label,
  chosen,
  onPick,
}: {
  label: string
  chosen: string[]
  onPick: (hit: VariantHit) => void
}): ReactNode {
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const { data, isFetching } = useProductSearch(term, open)

  const picked = useMemo(() => new Set(chosen), [chosen])

  if (!open) {
    return (
      <button type="button" className="btn btn--sm btn--ghost" onClick={() => setOpen(true)}>
        <PlusIcon size={15} />
        {label}
      </button>
    )
  }

  return (
    <div className="picker">
      <SearchInput value={term} onChange={setTerm} placeholder="Search your products…" />

      <ul className="picker__results">
        {isFetching && (data ?? []).length === 0 ? (
          <li className="picker__note">Searching…</li>
        ) : (data ?? []).length === 0 ? (
          <li className="picker__note">Nothing matched “{term}”.</li>
        ) : (
          (data ?? []).map((hit) => {
            const already = picked.has(hit.variant_id)

            return (
              <li key={hit.variant_id}>
                <button
                  type="button"
                  className="picker__row"
                  disabled={already}
                  onClick={() => {
                    onPick(hit)
                    setOpen(false)
                    setTerm('')
                  }}
                >
                  {hit.image ? <img src={hit.image} alt="" loading="lazy" /> : <span className="picker__blank" />}

                  <span className="picker__text">
                    {labelFor(hit)}
                    {/*
                      Out of stock is shown, not hidden. A merchant building next
                      week's bundle around something restocking on Monday should
                      be able to — the storefront drops it from the widget for as
                      long as it stays unavailable.
                    */}
                    <span className="table__sub">
                      {money(hit.price)}
                      {!hit.available && ' · out of stock'}
                    </span>
                  </span>

                  {already && <CheckIcon size={15} />}
                </button>
              </li>
            )
          })
        )}
      </ul>

      <button type="button" className="btn btn--sm btn--ghost" onClick={() => setOpen(false)}>
        Cancel
      </button>
    </div>
  )
}

function labelFor(hit: VariantHit): string {
  return hit.variant_title ? `${hit.title} — ${hit.variant_title}` : hit.title
}

function toItem(hit: VariantHit): BundleItem {
  return {
    variant_id: hit.variant_id,
    product_id: hit.product_id,
    handle: hit.handle,
    // Snapshotted for this table only. The storefront prefers the catalogue's
    // own copy, so a renamed product is right there and merely stale here.
    title: labelFor(hit),
    image: hit.image,
  }
}

function toDraft(bundle: Bundle): BundleDraft {
  const { id: _id, live: _live, discount_label: _label, ...draft } = bundle
  return draft
}

/**
 * An ISO instant as `datetime-local` wants it: local time, no zone, no seconds.
 *
 * Slicing the ISO string would show UTC in the box and shift a merchant's 6pm
 * end time by five and a half hours.
 */
function forInput(iso: string | null): string {
  if (!iso) return ''

  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''

  const pad = (n: number): string => String(n).padStart(2, '0')

  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  )
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min
  return Math.min(max, Math.max(min, Math.round(value)))
}
